import { Dispatch } from 'redux'

import {
  getAll,
  getHeadline,
  PutConfigValueBody,
  TPatchKpiTemplateSettingsBody,
  TPatchKpiTemplateCalculationBody,
  putTargetValues,
  updateCalculation,
  removeCalculation,
  updateSettings,
  create,
  destroy
} from 'redux/api/KPITemplates'
import { fetchData } from 'redux/api/Metrics'
import { parseApiErrors, TApiErrorEntry } from 'utils/errorParser'
import { useThunkActions } from '../useThunkActions'

import * as UserEvents from 'redux/api/internal/UserEvents'
import { Action } from 'types/GlobalUserEvents'

import { toggleSnackbarOpen } from 'redux/actions/Snackbar'
import { SnackbarMessage } from 'types/GlobalSnackbar'
import { selectGlobalFilterForApi } from 'redux/selectors/GlobalFilter'
import { ApplicationState } from 'redux/Stores/types'
import { KPI_UNIT } from 'types/Enums'
import {
  TDefinitionSlice,
  TPutMetricCalculationBlock,
  TMetricSettings,
  TMetricQueryParams,
  TMetricSourceRef
} from 'types/GlobalMetric'
import { getMetricDataKey, getMetricDefaultSettings } from 'utils/metric'

import * as actions from './actions'
import { tryExportDataXlsx } from '../Utilities'

/** The skärningar a 409 `slice_not_activated` names, one per entry. */
function getMissingSlices(errors: TApiErrorEntry[]): TDefinitionSlice[] {
  return errors
    .filter((entry) => entry.code === 'slice_not_activated')
    .map((entry) => {
      const meta = entry.meta as {
        entity_id: string
        slice_id: string
        slice_name: string
      }

      return {
        configured: false,
        entity_id: meta.entity_id,
        id: meta.slice_id,
        name: meta.slice_name
      }
    })
}

export function tryGetAllMetrics() {
  return (dispatch: Dispatch) => {
    dispatch(actions.getAll())

    getAll()
      .then((response) => dispatch(actions.getAllSuccess(response.data.data)))
      .catch(() => dispatch(actions.getAllFailed()))
  }
}

export function tryGetHeadlineMetric() {
  return (dispatch: Dispatch) => {
    dispatch(actions.getHeadline())

    getHeadline()
      .then((response) =>
        dispatch(actions.getHeadlineSuccess(response.data.data))
      )
      .catch(() => dispatch(actions.getHeadlineFailed()))
  }
}

function tryUpdateMetricSettings(
  id: string,
  body: TPatchKpiTemplateSettingsBody
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.update())

    updateSettings(id, { data: body })
      .then((response) => {
        UserEvents.log({
          action: Action.METRIC_SETTINGS_EDITED,
          context: null,
          dashboard_id: null,
          dashboard_group_id: null
        })

        dispatch(actions.updateSuccess(response.data.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.MetricUpdated))
      })
      .catch(() => {
        dispatch(actions.updateFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.MetricUpdatedFailed))
      })
  }
}

function tryPutConfigValue(id: string, body: PutConfigValueBody) {
  return (dispatch: Dispatch) => {
    dispatch(actions.putConfig())

    putTargetValues(id, body)
      .then((response) =>
        dispatch(actions.putConfigSuccess(response.data.data))
      )
      .catch(() => dispatch(actions.putConfigFailed()))
  }
}

function tryFetchMetricData(
  id: string,
  settings: TMetricSettings,
  queryParams?: TMetricQueryParams,
  source?: TMetricSourceRef,
  includeKeyFigure?: boolean
) {
  return (dispatch: Dispatch, getState: () => ApplicationState) => {
    const state = getState()
    const globalFilter = selectGlobalFilterForApi(state)
    const metric = state.MetricStore.data[id]

    const defaultSettings = getMetricDefaultSettings(
      metric,
      settings.variant_kpi_template_id
    )

    const body = {
      ...settings,
      period_filter: defaultSettings?.period_filter,
      query_params: queryParams,
      source,
      include_key_figure: includeKeyFigure
    }

    // Skip if an identical request is already in flight (e.g. several
    // components asking for the same slice before the first response lands).
    const dataKey = getMetricDataKey(body)

    if (state.MetricStore.fetchingRawData[id]?.[dataKey]) {
      return
    }

    dispatch(actions.getOneRaw({ id, body }))

    fetchData(id, globalFilter, body)
      .then((response) =>
        dispatch(
          actions.getOneRawSuccess({
            data: response.data.data,
            id,
            body
          })
        )
      )
      .catch((e) =>
        dispatch(
          actions.getOneRawFailed({
            id,
            body,
            missingSlices: getMissingSlices(parseApiErrors(e))
          })
        )
      )
  }
}

function tryCreateCustomMetric(body: {
  data: {
    name: string
    unit: KPI_UNIT
    blocks: TPutMetricCalculationBlock
  }
}) {
  return (dispatch: Dispatch) => {
    create(body)
      .then((response) => {
        dispatch(actions.createSuccess(response.data.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.MetricCreated))
      })
      .catch(() => {
        dispatch(toggleSnackbarOpen(SnackbarMessage.MetricCreatedFailed))
      })
  }
}

function tryUpdateMetricCalculation(
  id: string,
  body: TPatchKpiTemplateCalculationBody
) {
  return (dispatch: Dispatch) => {
    return updateCalculation(id, { data: body })
      .then((response) => {
        dispatch(actions.updateSuccess(response.data.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.MetricUpdated))
      })
      .catch(() => {
        dispatch(toggleSnackbarOpen(SnackbarMessage.MetricUpdatedFailed))
      })
  }
}

function tryRemoveMetricDefinition(id: string) {
  return (dispatch: Dispatch) => {
    removeCalculation(id)
      .then((response) => {
        dispatch(actions.updateSuccess(response.data.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.MetricDefinitionRemoved))
      })
      .catch(() => {
        dispatch(
          toggleSnackbarOpen(SnackbarMessage.MetricDefinitionRemovedFailed)
        )
      })
  }
}

function tryDeleteCustomMetric(id: string) {
  return (dispatch: Dispatch) => {
    destroy(id)
      .then(() => {
        dispatch(actions.deleteSuccess(id))

        dispatch(toggleSnackbarOpen(SnackbarMessage.MetricDeleted))
      })
      .catch(() => {
        dispatch(toggleSnackbarOpen(SnackbarMessage.MetricDeletedFailed))
      })
  }
}

function tryExportMetricDataXlsx(
  headers: string[],
  rows: { [key: string]: string | number | null }[],
  fileName: string
) {
  return (dispatch: Dispatch) => {
    // Call the thunk directly with dispatch
    const exportThunk = tryExportDataXlsx(headers, rows, fileName)

    exportThunk(dispatch)
  }
}

export const useMetricActions = () => {
  return {
    tryGetAllMetrics: useThunkActions(tryGetAllMetrics),
    tryUpdateMetricSettings: useThunkActions(tryUpdateMetricSettings),
    tryPutConfigValue: useThunkActions(tryPutConfigValue),
    tryFetchMetricData: useThunkActions(tryFetchMetricData),
    tryCreateCustomMetric: useThunkActions(tryCreateCustomMetric),
    tryUpdateMetricCalculation: useThunkActions(tryUpdateMetricCalculation),
    tryRemoveMetricDefinition: useThunkActions(tryRemoveMetricDefinition),
    tryDeleteCustomMetric: useThunkActions(tryDeleteCustomMetric),
    tryExportMetricDataXlsx: useThunkActions(tryExportMetricDataXlsx)
  }
}
